/**
 * Tasks: the report flag on a table row and its mark once reported.
 *
 * The HOST provides a {@link RowFlagAdapter} through `DashboardBody`'s
 * `rowFlags` prop (the portal does; the builder Preview does not, so no
 * flag ever renders there). The package only knows how to IDENTIFY a row
 *, by the table's `row_identity` fields, never by position, and how to
 * draw the two states:
 *
 * - not reported: a square flag button, the size of «Ver detalle», that
 *   appears on row hover or keyboard focus (always visible on touch
 *   screens); its name says «Reportar»;
 * - reported: the same square with a FILLED flag, or a ✓ once resolved
 *   (shape, not colour alone), and the status in WORDS for screen
 *   readers and the tooltip ("Reportado · En curso", "Resuelto").
 *
 * Icon only, on purpose: a word-sized chip per row widened every table
 * and never lined up with «Ver detalle» (Pablo, 2026-10-30).
 *
 * A row whose identity has an empty field gets no flag: there is nothing
 * honest to anchor to.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import type { ColumnDef } from "./DataTable";

/** WHERE a row lives: enough to find it again later. */
export interface RowAnchorRef {
  /** Dashboard table id (`main` for the legacy single table). For a
   *  drill row, the PARENT table whose row opened the drill. */
  tableId: string;
  /** Drill id when the row belongs to a row-drill table, else null. */
  drillId: string | null;
  /** The parent row's drill value (drill rows only), else null. */
  rowValue: string | null;
  /** The identity fields and their values. */
  identity: Record<string, string | number>;
}

/** A row the user wants to report: where it is plus what it showed. */
export interface RowAnchor extends RowAnchorRef {
  row: Record<string, unknown>;
  /** The columns as the table draws them, the ones a pivot discovers
   *  included (the config does not name those), or null for a drill row,
   *  whose cells are keyed by their label. */
  columns: ColumnDef[] | null;
}

export interface RowMarkState {
  taskId: string;
  /** Task status (`new`, `in_progress`, `in_review`, `done`). */
  status: string;
}

/** A KPI, a whole chart or one point of a chart the user wants to report. */
export interface ItemAnchor {
  anchorType: "kpi" | "chart" | "point";
  /** The KPI or chart as the reader names it (its title). */
  itemLabel: string;
  /** The authored item id, when the config has one. */
  itemId?: string;
  /** What was on screen: the KPI value, or the point picked. */
  shown?: Record<string, unknown>;
}

export interface RowFlagAdapter {
  /** The mark of an already reported row, or null. */
  markFor(anchor: RowAnchorRef): RowMarkState | null;
  /** The user clicked "Reportar" on a row. */
  onFlag(anchor: RowAnchor): void;
  /** The user clicked the mark of a reported row. */
  onOpen(taskId: string): void;
  /** The user flagged a KPI, a chart or a point of a chart. Absent = no
   *  flag on KPIs and charts (rows only). */
  onFlagItem?(anchor: ItemAnchor): void;
}

const RowFlagContext = createContext<RowFlagAdapter | null>(null);

export function RowFlagProvider({
  value,
  children,
}: {
  value: RowFlagAdapter | null;
  children: ReactNode;
}): JSX.Element {
  return <RowFlagContext.Provider value={value}>{children}</RowFlagContext.Provider>;
}

export function useRowFlags(): RowFlagAdapter | null {
  return useContext(RowFlagContext);
}

/** The identity of `row`, or null when a field is missing or empty. */
export function rowIdentity(
  row: Record<string, unknown>,
  fields: readonly string[],
): Record<string, string | number> | null {
  if (fields.length === 0) return null;
  const out: Record<string, string | number> = {};
  for (const field of fields) {
    const value = row[field];
    if (typeof value === "number" && Number.isFinite(value)) {
      out[field] = value;
    } else if (typeof value === "string" && value.trim() !== "") {
      out[field] = value;
    } else {
      return null;
    }
  }
  return out;
}

/** Stable key of an anchor, the same on both sides of the adapter. */
export function anchorRefKey(ref: RowAnchorRef): string {
  const identity = Object.keys(ref.identity)
    .sort()
    .map((k) => [k, String(ref.identity[k])]);
  return JSON.stringify([ref.tableId, ref.drillId ?? "", ref.rowValue ?? "", identity]);
}

function FlagGlyph({ filled = false }: { filled?: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width={12}
      height={12}
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1.6}
      aria-hidden="true"
    >
      <path d="M3 14V2m0 1h8l-1.5 3L11 9H3" />
    </svg>
  );
}

/**
 * The flag of a KPI or chart card: a small button in the bottom-right
 * corner, shown on hover or keyboard focus (always on touch screens).
 *
 * A chart whose clicks already mean something (cross-filter) offers two
 * choices: the whole chart, or «pick a point», after which the NEXT click
 * on the chart picks instead of filtering (the host shows how to leave
 * that mode; Escape leaves it too).
 */
export function ItemFlag({
  onFlagWhole,
  onPickPoint,
}: {
  onFlagWhole: () => void;
  onPickPoint?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [menu, setMenu] = useState(false);
  const stop = (e: MouseEvent): void => {
    e.stopPropagation();
  };
  return (
    <div
      className="absolute bottom-2 right-2 z-10"
      onClick={stop}
      role="presentation"
    >
      <button
        type="button"
        data-item-flag=""
        aria-haspopup={onPickPoint ? "menu" : undefined}
        aria-expanded={onPickPoint ? menu : undefined}
        onClick={() => (onPickPoint ? setMenu((v) => !v) : onFlagWhole())}
        className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2 py-0.5 text-2xs font-medium text-neutral-700 opacity-0 shadow-sm transition-opacity duration-150 hover:border-accent hover:text-accent-dark focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
      >
        <FlagGlyph />
        {t("tasks.mark.flag")}
      </button>
      {menu && onPickPoint && (
        <div
          role="menu"
          className="absolute bottom-full right-0 mb-1 w-48 overflow-hidden rounded-lg border border-neutral-200 bg-white text-xs shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-2 text-left hover:bg-neutral-50"
            onClick={() => {
              setMenu(false);
              onFlagWhole();
            }}
          >
            {t("tasks.mark.wholeChart")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full border-t border-neutral-100 px-3 py-2 text-left hover:bg-neutral-50"
            onClick={() => {
              setMenu(false);
              onPickPoint();
            }}
          >
            {t("tasks.mark.pickPoint")}
          </button>
        </div>
      )}
    </div>
  );
}

/** The banner of «pick a point» mode, over the chart it applies to. */
export function PickPointBanner({ onCancel }: { onCancel: () => void }): JSX.Element {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div
      role="status"
      data-pick-point=""
      className="absolute inset-x-2 top-2 z-10 flex items-center justify-between gap-2 rounded-lg border border-dashed border-accent bg-accent/10 px-3 py-1.5 text-xs font-semibold text-accent-dark"
    >
      <span>⚑ {t("tasks.mark.pickHint")}</span>
      <button type="button" onClick={onCancel} className="underline">
        {t("tasks.mark.pickCancel")}
      </button>
    </div>
  );
}

/** The cell content for one row: flag button or reported mark. */
export function RowMark({
  adapter,
  anchorRef,
  row,
  columns,
}: {
  adapter: RowFlagAdapter;
  anchorRef: RowAnchorRef;
  row: Record<string, unknown>;
  columns: ColumnDef[] | null;
}): JSX.Element {
  const { t } = useTranslation();
  const mark = adapter.markFor(anchorRef);
  const stop = (e: MouseEvent): void => {
    e.stopPropagation();
  };
  const square =
    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-xs leading-none transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary max-sm:h-10 max-sm:w-10";
  if (mark) {
    const resolved = mark.status === "done";
    const label = resolved
      ? t("tasks.mark.resolved")
      : `${t("tasks.mark.reported")} · ${t(`tasks.status.${mark.status}`)}`;
    return (
      <button
        type="button"
        data-row-mark={resolved ? "resolved" : "open"}
        title={label}
        onClick={(e) => {
          stop(e);
          adapter.onOpen(mark.taskId);
        }}
        className={`${square} ${
          resolved
            ? "border-primary/30 bg-primary-soft text-primary-ink"
            : "border-amber-300 bg-amber-50 text-amber-900"
        }`}
      >
        {resolved ? <span aria-hidden="true">✓</span> : <FlagGlyph filled />}
        <span className="sr-only">{label}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      data-row-flag=""
      aria-label={t("tasks.mark.flag")}
      title={t("tasks.mark.flag")}
      onClick={(e) => {
        stop(e);
        adapter.onFlag({ ...anchorRef, row, columns });
      }}
      className={`${square} border-control bg-white text-neutral-600 opacity-0 hover:border-accent hover:text-accent-dark focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100`}
    >
      <FlagGlyph />
    </button>
  );
}
